import * as Actions from '../actions/matterActions';
import Matter from '../../entities/matter';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {getSessionFirmId, getSessionUserId} from '../../lib/picos/sessionIdentity';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type MatterAction = {
    type: string;
    matter: Matter | null; 
    matterId: string | Matter; 
    partial: boolean; 
    lastFetch: number; 
    matters: Matter[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Matter> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Matter[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Matter> | null, firmId: string, userId: string, updates?: Matter[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'matter', 'matters', data ? backupRows(data) : null, updates)
		  .then(result => {
			  // OWA-786 (CXR-4-001): the worker resolves a typed result for every
			  // path — a resolution is NOT an acknowledgement. Only ok: true means
			  // the backup write is durable; anything else is surfaced, never
			  // swallowed, so a skipped or failed write cannot masquerade as one.
			  if (!result?.ok) console.warn(`⮂ matterReducer: backup write for 'matters' did not persist (${result?.reason ?? 'unknown'}): ${result?.message ?? ''}`);
		  })
		  .catch(console.error);
}

/**
 * The identity that names this tab's backup store, or null when this tab has
 * not finished authenticating.
 *
 * OWA-785: this must come from sessionStorage (per tab), never from
 * localStorage (shared by every tab on the origin). With two firms open in two
 * tabs, the localStorage value is whichever firm logged in last, so the other
 * tab persisted its entities into that firm's backup store and they restored
 * into the wrong firm's UI. A partial identity names some other store, so we
 * skip the write rather than misfile it.
 *
 * @returns {{firmId: string, userId: string} | null}
 */
function storageIdentity(): {firmId: string; userId: string} | null {
	const firmId = getSessionFirmId();
	const userId = getSessionUserId();

	if (!firmId || !userId) {
		if (logging) console.debug('⮂ matterReducer: no session identity, skipping backup write');
		return null;
	}

	return {firmId, userId};
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Matter>} data
 * @param {Matter[]} [updates]
 * @returns {ArrayMap<Matter>}
 */
function updateStorage(data: ArrayMap<Matter>, updates?: Matter[]): ArrayMap<Matter> {
	if (logging) console.debug('⮂ matterReducer: updateStorage');

	if (updates && updates.length === 0) return data;

	const identity = storageIdentity();
	if (!identity) return data;

	if (updates) callStorageWorker(null, identity.firmId, identity.userId, updates);
	else callStorageWorker(data, identity.firmId, identity.userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Matter[]} [updates]
 * @returns {ArrayMap<Matter>}
 */
function updateStorageNoReturn(updates?: Matter[]): void {
	if (logging) console.debug('⮂ matterReducer: updateStorageNoReturn');

	if (!updates || updates.length === 0) return;

	const identity = storageIdentity();
	if (!identity) return;

	callStorageWorker(null, identity.firmId, identity.userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Matter[]} [data]
 * @returns {ArrayMap<Matter>}
 */
function createState(data?: Matter[] | ArrayMap<Matter>): ArrayMap<Matter> {
	if (!data) return ArrayMapRegistry.clone<Matter>('matter', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Matter>('matter', data.map(e => e));
    else return ArrayMapRegistry.clone<Matter>('matter', data);
}

// Whether deleted matters keep their rows (schema includeDeletedInResults). OWA-68
const retainDeleted = false;

/**
 * The full backup snapshot of a collection: one entry per ID. A row when there is one (deleted rows of a
 * type that keeps them included); otherwise a deletion record for an ID whose row was removed. OWA-68
 *
 * @param {ArrayMap<Matter>} data
 * @returns {Array<Matter | MatterDeletionRecord>}
 */
function backupRows(data: ArrayMap<Matter>): Array<Matter | MatterDeletionRecord> {
	const records: MatterDeletionRecord[] = data.deletedVersionEntries()
		.filter(([id]) => !data.has(id))
		.map(([id, addedOn]) => ({matterId: id, addedOn, status: 'D', deletionRecord: true}));

	return records.length ? [...data.values(), ...records] : data.values();
}

/**
 * A deletion record read back from the offline backup: evidence that a matter was deleted at a
 * version, never a live row. The storage worker writes one for a deleted matter whose type does
 * not keep deleted rows. OWA-68
 */
type MatterDeletionRecord = {matterId: string; addedOn: number; status: 'D'; deletionRecord: true};

const isDeletionRecord = (o: unknown): o is MatterDeletionRecord => (o as MatterDeletionRecord)?.deletionRecord === true;

/**
 * Apply one incoming version of a matter to a collection the caller owns (a clone), under the one
 * acceptance rule every path uses (OWA-68):
 * - older than the stored row: rejected
 * - an active version equal to a kept deleted row: rejected (it yields only to a strictly newer one)
 * - no stored row, and at or below the recorded deletion version: rejected
 * - an accepted deleted version records its deletion version; the row is kept as a deleted row when
 *   this type keeps deleted rows, otherwise it is removed
 * - an accepted active version is stored (a newer one supersedes the deletion version)
 *
 * @param {ArrayMap<Matter>} st
 * @param {Matter | MatterDeletionRecord} incoming
 * @returns {boolean} whether the version was accepted; only accepted versions go to the backup
 */
function applyVersion(st: ArrayMap<Matter>, incoming: Matter | MatterDeletionRecord): boolean {
	const id = isDeletionRecord(incoming) ? incoming.matterId : incoming.id;
	const current = st.get(id);

	if (current) {
		if (incoming.addedOn < current.addedOn) return false;
		// A kept deleted row yields only to a strictly newer active version, like a deletion version
		if (current.status === 'D' && incoming.status !== 'D' && incoming.addedOn === current.addedOn) return false;
	}
	else {
		const deletedAt = st.deletedVersion(id);
		if (deletedAt !== undefined && incoming.addedOn <= deletedAt) return false;
	}

	if (incoming.status === 'D') {
		st.recordDeletion(id, incoming.addedOn);
		if (retainDeleted && !isDeletionRecord(incoming)) st.set(incoming);
		else if (current) st.delete(current);
	}
	else st.set(incoming as Matter);

	return true;
}

/**
 * A new indexed collection holding the rows and deletion versions of the given one. Fetch paths build
 * from this instead of cloning, because the initial store state is a bare ArrayMap without the
 * schema's indexes. OWA-68
 *
 * @param {ArrayMap<Matter>} from
 * @returns {ArrayMap<Matter>}
 */
function carryOver(from: ArrayMap<Matter>): ArrayMap<Matter> {
	const next = createState(from);
	from.deletedVersionEntries().forEach(([id, version]) => next.recordDeletion(id, version));
	return next;
}

function matters(state: ArrayMap<Matter>, action: MatterAction): ArrayMap<Matter> {
	if (!state) state = createState();

	let st: ArrayMap<Matter>;

	switch (action.type) {
		// action.matter - the new value of matter with id action.id
		// action.matterId - id of the target, used if new value is null
		case Actions.MATTER_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ matterReducer: FETCH_ONE_SUCCESS', action);

			if (action.matter === null) return state;

			// Only for a matter this collection knows (a row, or a deletion version), and only when the
			// fetched version is accepted (OWA-68).
			if (state.has(action.matterId) || state.deletedVersion(action.matter.id) !== undefined) {
				st = state.clone();
				if (!applyVersion(st, action.matter)) return state;

				updateStorageNoReturn([action.matter]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.matters
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.matters
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.matters
			 !partial, !stateEmpty, fetchEmpty	-> D return matters in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge matters in state newer than those in action.matters
		 */
		case Actions.MATTER_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.matters.length === 0;
			// A deletion record from the backup is evidence only: never constructed into an entity (OWA-68)
			const fetched: Array<Matter | MatterDeletionRecord> = action.matters.map(matter => isDeletionRecord(matter) ? matter : new Matter({...matter, persisted: true}));
			const fetchedIds = new Set(fetched.map(matter => isDeletionRecord(matter) ? matter.matterId : matter.id));

			// A full fetch is the whole dataset, so a persisted active row it omits is dropped (deleted rows,
			// deletion versions and unsaved local rows stay). OWA-68
			const dropOmitted = (st: ArrayMap<Matter>) => {
				[...st.values()].forEach(row => {
					if (!fetchedIds.has(row.id) && row.persisted && row.status !== 'D') st.delete(row);
				});
			};

			if (logging) console.debug(`⮂ matterReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} matters with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ matterReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Build a new indexed state that keeps any deletion versions (the visible state is
					// empty, but a deletion version can still reject an older row). OWA-68
					st = carryOver(state);
					fetched.forEach(matter => applyVersion(st, matter));

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ matterReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Matter> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Matter[] = [];

				// Partials include deleted rows for every entity; applyVersion keeps or removes them and
				// records their deletion version (OWA-68). Only accepted versions go to the backup.
				fetched.forEach(matter => {
					if (applyVersion(st, matter)) toUpdate.push(matter as Matter);
					else if (logging) console.debug('⮂ matterReducer: partialMerge: rejected older version of matter');
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ matterReducer: empty: matters', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Drop the persisted active rows; deleted rows, deletion versions and unsaved local rows
				// stay (OWA-68)
				st = carryOver(state);
				dropOmitted(st);

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ matterReducer: fullMerge: matters', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Start from the current rows and deletion versions, apply each fetched version under the one
				// rule, then drop persisted active rows the full fetch omitted (OWA-68). A fetched row replaces
				// a local one unless the local one is strictly newer, so a stale local (persisted or not) never
				// shadows a newer server row (OWA-7 #1), and a newer row that arrived while the fetch was in
				// flight (for example a socket update during startup restore) is kept.
				st = carryOver(state);
				fetched.forEach(matter => applyVersion(st, matter));
				dropOmitted(st);

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.MATTER_FETCH_ONE_FAILURE:
			console.error('MATTER_FETCH_ONE_FAILURE', action.matterId, action.err);

			// No update to storage required
			return state;

		case Actions.MATTER_FETCH_FAILURE:
			console.error('MATTER_FETCH_FAILURE', action.matterId, action.err);

			// No update to storage required
			return state;

		case Actions.MATTER_RESTORE: {
		    let matter = new Matter({...action.matter});
            st = state.clone();

            // One acceptance rule for every incoming version (OWA-68)
            if (!applyVersion(st, matter)) return state;

            return updateStorage(st, [matter]);
        }
            
		case Actions.MATTER_SAVE: {
		    // console.debug('⮂ MATTER_SAVE reducer', action.matter?.id);
		    
			// clone the matter
			let matter = new Matter({...action.matter});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new matter in the state, under the one acceptance rule (OWA-68). A rejected
			// version changes nothing and is not written to the backup.
			if (!applyVersion(st, matter)) return state;

			return updateStorage(st, [matter]);
		}

		case Actions.MATTER_SAVE_MANY: {
		    if (logging) console.debug('⮂ MATTER_SAVE_MANY reducer', action.matters.map(matter => matter.id).join(','));
		    
			// clone the matters
			let toProcess = action.matters.map(matter => new Matter(matter));

			st = state.clone();
			// state.clear();

			const updates: Matter[] = [];

			toProcess.forEach(matter => {
				// Add or update the new matter in the state, under the one acceptance rule (OWA-68)
				if (applyVersion(st, matter)) updates.push(matter);
				else if (logging) console.debug('⮂ MATTER_SAVE_MANY: rejected older version of matter');
			});

			return updateStorage(st, updates);
		}

		case Actions.MATTER_REMOVE: {
			// clone the matter; a remove is a deleted version (the remove saga stamps it 'D' and a new
			// addedOn)
			const matter = new Matter({...action.matter, status: 'D'});

			// Remove from UI under the one acceptance rule: the deletion version is recorded, and the row
			// is removed, or kept as a deleted row for a type that keeps them (OWA-68)
			st = state.clone();
			if (!applyVersion(st, matter)) return state;

			return updateStorage(st, [matter]);
		}

		case Actions.MATTER_RESTORE_FAILURE:
		case Actions.MATTER_SAVE_FAILURE: {
		    console.warn('MATTER_SAVE_FAILURE reducer', action.matter?.id);
		    if (!action.matter?.id) {
				console.warn('MATTER_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const matter = ensure(Matter, action.matter);
			if (!matter) {
				console.error('Could not find matter after call to save');
				return state;
			}

			st = state.clone();

			const _matterRef = state.get(matter.id);

			if (_matterRef && (_matterRef.addedOn === matter.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _matter = new Matter({..._matterRef});
				_matter.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_matter.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.update('matter', matter.addedOn, {fatalError: _matter.fatalError}).then(r => {
						if (r.isErr()) console.error(`⮂ matterReducer: failed to save matter with fatalError info: ${r.unwrapErr().message}`);
						else if (r.unwrap() === 0) {
							// Non-document 4xx handlers may remove the row before dispatch.
							// Preserve the historical rejected-operation queue behavior.
							idb.add('matter', _matter).then(saved => {
								if (saved.isErr()) console.error('Could not restore rejected matter: ' + saved.unwrapErr().message);
							}).catch(err => console.error('Could not restore rejected matter: ' + err.message));
						}
						else console.debug('⮂ matterReducer: saved matter with fatalError info');
					}).catch(err => console.error(`⮂ matterReducer: failed to save matter with fatalError info: ${err.message}`));

				st.set(_matter);
			}

			return updateStorage(st);
		}

		case Actions.MATTER_SAVE_MANY_FAILURE: {
		    console.warn('MATTER_SAVE_MANY_FAILURE reducer', action.matters.map(matter => matter.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.MATTER_RESTORE_SUCCESS:
		case Actions.MATTER_SAVE_SUCCESS: {
			if (!action.matter?.id) {
				console.warn('MATTER_SAVE_SUCCESS reducer');
                return state;
			}

			const matterRef = state.get(action.matter?.id);
			if (!matterRef) {
				console.warn('Could not find matter after call to save');
                return state;
			}

			st = state.clone();

			// The echoed version goes through the one acceptance rule (OWA-68). When it is older than the
			// current row, only the current row's persisted flag changes, and the current row (never the
			// rejected echo) is what the backup receives.
			if (applyVersion(st, action.matter)) return updateStorage(st, [action.matter]);

			if (matterRef.persisted) return state;

			// Create a new instance to avoid mutating the entity shared with old state
			const matter = new Matter({...matterRef, persisted: true});
			st.set(matter);

			return updateStorage(st, [matter]);
		}

		case Actions.MATTER_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ MATTER_SAVE_MANY_SUCCESS reducer', action.matters.map(matter => matter.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const matters: Matter[] = [];

			action.matters.forEach(e => {
				const matterRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!matterRef) console.warn('Could not find matter after call to save');
				// The echoed version goes through the one acceptance rule (OWA-68)
				else if (applyVersion(st, e)) matters.push(e);
				else if (!matterRef.persisted) {
					// Older echo: only the current row's persisted flag changes, and the current row is
					// what the backup receives. Create a new instance to avoid mutating the entity shared
					// with old state.
					const matter = new Matter({...matterRef, persisted: true});
					st.set(matter);
					matters.push(matter);
				}
			});

			return updateStorage(st, matters);
		}

		case Actions.MATTER_REMOVE_FAILURE: {
		    console.warn('MATTER_REMOVE_FAILURE action', action.matter?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.MATTER_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.MATTER_RESTORE_MANY: {
			if (logging) console.debug('⮂ MATTER_RESTORE_MANY reducer', action.matters.map(matter => matter.id).join(','));

			const toProcess = action.matters.map(matter => new Matter({...matter}));
			st = state.clone();

			const updates: Matter[] = [];

			// One acceptance rule for every incoming version (OWA-68): a deleted version removes the row and
			// records its deletion version, or keeps a deleted row for a type that keeps them.
			toProcess.forEach(matter => {
				if (applyVersion(st, matter)) updates.push(matter);
				else if (logging) console.debug('⮂ MATTER_RESTORE_MANY: rejected older version of matter');
			});

			return updateStorage(st, updates);
		}

		case Actions.MATTER_REMOVE_MANY: {
			if (logging) console.debug('⮂ MATTER_REMOVE_MANY reducer', action.matters.map(matter => matter.id).join(','));

			// A socket delete is a deleted version, under the one acceptance rule (OWA-68)
			const toProcess = action.matters.map(matter => new Matter({...matter, status: 'D'}));
			st = state.clone();

			const updates: Matter[] = [];

			toProcess.forEach(matter => {
				if (applyVersion(st, matter)) updates.push(matter);
				else if (logging) console.debug('⮂ MATTER_REMOVE_MANY: rejected older version of matter');
			});

			return updateStorage(st, updates);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default matters;